import type { AxiosInstance } from 'axios';
import type { OpportunityClient } from '../interfaces/opportunity';
import type {
    AdvanceOpportunityPayload,
    CloseOpportunityPayload,
    CreateProspectPayload,
    OpportunityDetail,
    UpdateOpportunityPayload,
} from '../interfaces/opportunityDetail';

/**
 * Nav: **Opportunity** — reads/writes for the wizard, plus `POST /prospects`. See
 * docs/CRM_SECTION_CONTRACT.md §5 and §6.
 *
 * Reached as `getCrmRequests().opportunityDetail.<method>()`.
 *
 * **A failed call rejects** — the shell's interceptor toasts the server error, then rethrows
 * (FRONTEND_PATTERNS.md) — so the stepper can never advance on a failed save: the store's `catch` is the
 * one failure branch.
 */
export class OpportunityDetailApi {
    protected readonly client: AxiosInstance;

    protected readonly baseURL: string;

    constructor(client: AxiosInstance, baseURL: string) {
        this.client = client;
        this.baseURL = baseURL;
    }

    private opportunityUrl(opportunityId: string): string {
        return `${this.baseURL}/opportunities/${opportunityId}`;
    }

    async get(opportunityId: string): Promise<OpportunityDetail> {
        const response = await this.client.get<OpportunityDetail>(this.opportunityUrl(opportunityId));
        return response.data;
    }

    async createEmpty(configurationId: string): Promise<OpportunityDetail> {
        const response = await this.client.post<OpportunityDetail>(`${this.baseURL}/opportunities`, {
            configuration: configurationId,
        });
        return response.data;
    }

    /** `Enregistrer le brouillon`. */
    async update(
        opportunityId: string,
        payload: UpdateOpportunityPayload,
    ): Promise<OpportunityDetail> {
        const response = await this.client.patch<OpportunityDetail>(
            this.opportunityUrl(opportunityId),
            payload,
        );
        return response.data;
    }

    /** `Valider et passer à l'étape suivante` — saves the values and advances, or `400`s. */
    async advance(
        opportunityId: string,
        payload: AdvanceOpportunityPayload,
    ): Promise<OpportunityDetail> {
        const response = await this.client.post<OpportunityDetail>(
            `${this.opportunityUrl(opportunityId)}/advance`,
            payload,
        );
        return response.data;
    }

    /** The server refuses the block's last entry. */
    async deleteEntry(opportunityId: string, blockId: string, instance: string): Promise<OpportunityDetail> {
        const response = await this.client.delete<OpportunityDetail>(
            `${this.opportunityUrl(opportunityId)}/blocks/${blockId}/entries/${instance}`,
        );
        return response.data;
    }

    /** `Terminer l'opportunité` — the `Gagnée` / `Perdue` result. */
    async close(
        opportunityId: string,
        payload: CloseOpportunityPayload,
    ): Promise<OpportunityDetail> {
        const response = await this.client.post<OpportunityDetail>(
            `${this.opportunityUrl(opportunityId)}/close`,
            payload,
        );
        return response.data;
    }

    /**
     * `Type d'opportunité: Prospect` — writes a `Client` row with `prospect: true` into the `aquassist`
     * database, not into `aquassist-crm`. Read §5 before implementing: this is the one call that can leak
     * a prospect into existing client lists, search, billing and ERP sync.
     */
    async createProspect(payload: CreateProspectPayload): Promise<OpportunityClient> {
        const response = await this.client.post<OpportunityClient>(
            `${this.baseURL}/prospects`,
            payload,
        );
        return response.data;
    }
}
